import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

import { cn } from '../../utils/cn';

// Values are always "YYYY-MM-DD" (or "YYYY-MM" for months); only the display is dd/mm/yyyy.

const pad = (value: number) => String(value).padStart(2, '0');

export function toDateKey(year: number, monthIndex: number, day: number) {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
}

export function todayDateKey(now = new Date()) {
  return toDateKey(now.getFullYear(), now.getMonth(), now.getDate());
}

export function formatDisplayDate(dateKey: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateKey ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

export function formatDisplayMonth(monthKey: string) {
  const match = /^(\d{4})-(\d{2})/.exec(monthKey ?? '');
  return match ? `${match[2]}/${match[1]}` : '';
}

function isRealDate(year: number, month: number, day: number) {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

// Accepts "18/6/2026", "18-06-2026", "18.06.2026" and "18062026"; returns null for anything else.
export function parseDisplayDate(text: string): string | null {
  const trimmed = text.trim();
  let parts: string[];
  if (/^\d{8}$/.test(trimmed)) {
    parts = [trimmed.slice(0, 2), trimmed.slice(2, 4), trimmed.slice(4)];
  } else {
    parts = trimmed.split(/[/.\-\s]+/);
  }
  if (parts.length !== 3 || parts[2].length !== 4) return null;
  const [day, month, year] = parts.map(Number);
  if (!isRealDate(year, month, day)) return null;
  return toDateKey(year, month - 1, day);
}

// "6/2026", "06-2026" or "062026".
export function parseDisplayMonth(text: string): string | null {
  const trimmed = text.trim();
  const parts = /^\d{6}$/.test(trimmed) ? [trimmed.slice(0, 2), trimmed.slice(2)] : trimmed.split(/[/.\-\s]+/);
  if (parts.length !== 2 || parts[1].length !== 4) return null;
  const [month, year] = parts.map(Number);
  if (!(month >= 1 && month <= 12) || year < 1900) return null;
  return `${year}-${pad(month)}`;
}

// Adds the slash after the day and month while typing digits.
function autoSlash(previous: string, next: string, groups: number) {
  if (next.length <= previous.length) return next;
  const slashes = (next.match(/\//g) ?? []).length;
  if (slashes >= groups) return next;
  return /(^|\/)\d{2}$/.test(next) ? `${next}/` : next;
}

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const POPOVER_WIDTH = 288;
const GAP = 6;

type View = 'days' | 'months' | 'years';

interface CalendarPanelProps {
  mode: 'date' | 'month';
  value: string;
  min?: string;
  max?: string;
  onSelect: (value: string) => void;
  onClear?: () => void;
}

function CalendarPanel({ mode, value, min, max, onSelect, onClear }: CalendarPanelProps) {
  const today = todayDateKey();
  const initial = /^\d{4}-\d{2}/.test(value) ? value : (max && max < today ? max : today);
  const [cursor, setCursor] = React.useState(() => ({
    year: Number(initial.slice(0, 4)),
    month: Number(initial.slice(5, 7)) - 1,
  }));
  const [view, setView] = React.useState<View>(mode === 'month' ? 'months' : 'days');

  const minMonth = min?.slice(0, 7);
  const maxMonth = max?.slice(0, 7);
  const outOfRange = (key: string, length: number) =>
    Boolean((min && key < min.slice(0, length)) || (max && key > max.slice(0, length)));

  const shift = (step: number) => {
    setCursor(current => {
      if (view === 'days') {
        const date = new Date(current.year, current.month + step, 1);
        return { year: date.getFullYear(), month: date.getMonth() };
      }
      return { ...current, year: current.year + step * (view === 'years' ? 12 : 1) };
    });
  };

  const yearStart = cursor.year - (cursor.year % 12);
  const heading = view === 'days'
    ? `Tháng ${cursor.month + 1}, ${cursor.year}`
    : view === 'months'
      ? String(cursor.year)
      : `${yearStart} – ${yearStart + 11}`;

  const navButton = 'flex h-8 w-8 items-center justify-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-academy-700';
  const cellBase = 'flex items-center justify-center rounded-lg text-sm transition-colors disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-transparent';

  let body: React.ReactNode;
  if (view === 'days') {
    const firstWeekday = (new Date(cursor.year, cursor.month, 1).getDay() + 6) % 7;
    const cells = Array.from({ length: 42 }, (_, index) => {
      const date = new Date(cursor.year, cursor.month, index - firstWeekday + 1);
      return { key: toDateKey(date.getFullYear(), date.getMonth(), date.getDate()), date };
    });
    // Skip the trailing week when the month fits in five.
    const visible = cells[35].date.getMonth() !== cursor.month ? cells.slice(0, 35) : cells;
    body = (
      <>
        <div className="grid grid-cols-7 pb-1 text-center text-[11px] font-semibold uppercase text-ink-400">
          {WEEKDAYS.map(day => <div key={day} className="py-1">{day}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {visible.map(({ key, date }) => {
            const inMonth = date.getMonth() === cursor.month;
            const selected = key === value;
            return (
              <button
                key={key}
                type="button"
                tabIndex={-1}
                disabled={outOfRange(key, 10)}
                onClick={() => onSelect(key)}
                aria-label={`Chọn ngày ${formatDisplayDate(key)}`}
                aria-pressed={selected}
                className={cn(
                  cellBase,
                  'h-9',
                  inMonth ? 'text-ink-800' : 'text-ink-300',
                  selected
                    ? 'bg-academy-600 font-semibold text-white hover:bg-academy-700'
                    : 'hover:bg-ink-100',
                  key === today && !selected && 'font-semibold text-academy-700 ring-1 ring-inset ring-academy-200',
                )}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </>
    );
  } else if (view === 'months') {
    body = (
      <div className="grid grid-cols-3 gap-1.5">
        {Array.from({ length: 12 }, (_, index) => {
          const key = `${cursor.year}-${pad(index + 1)}`;
          const selected = value.slice(0, 7) === key;
          return (
            <button
              key={key}
              type="button"
              tabIndex={-1}
              disabled={Boolean((minMonth && key < minMonth) || (maxMonth && key > maxMonth))}
              onClick={() => {
                if (mode === 'month') {
                  onSelect(key);
                } else {
                  setCursor({ year: cursor.year, month: index });
                  setView('days');
                }
              }}
              aria-pressed={selected}
              className={cn(
                cellBase,
                'h-11',
                selected ? 'bg-academy-600 font-semibold text-white hover:bg-academy-700' : 'text-ink-800 hover:bg-ink-100',
                key === today.slice(0, 7) && !selected && 'font-semibold text-academy-700 ring-1 ring-inset ring-academy-200',
              )}
            >
              Thg {index + 1}
            </button>
          );
        })}
      </div>
    );
  } else {
    body = (
      <div className="grid grid-cols-3 gap-1.5">
        {Array.from({ length: 12 }, (_, index) => {
          const year = yearStart + index;
          const selected = value.slice(0, 4) === String(year);
          return (
            <button
              key={year}
              type="button"
              tabIndex={-1}
              disabled={outOfRange(String(year), 4)}
              onClick={() => {
                setCursor({ year, month: cursor.month });
                setView('months');
              }}
              aria-pressed={selected}
              className={cn(
                cellBase,
                'h-11',
                selected ? 'bg-academy-600 font-semibold text-white hover:bg-academy-700' : 'text-ink-800 hover:bg-ink-100',
              )}
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  }

  const todayValue = mode === 'month' ? today.slice(0, 7) : today;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-1">
        <button type="button" tabIndex={-1} className={navButton} onClick={() => shift(-1)} aria-label="Trước">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setView(view === 'days' ? 'months' : 'years')}
          disabled={view === 'years'}
          className="rounded-lg px-2 py-1 text-sm font-semibold text-ink-900 transition-colors hover:bg-ink-100 disabled:hover:bg-transparent"
          aria-label="Chọn tháng hoặc năm"
        >
          {heading}
        </button>
        <button type="button" tabIndex={-1} className={navButton} onClick={() => shift(1)} aria-label="Sau">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {body}
      <div className="flex items-center justify-between border-t border-ink-200 pt-2">
        <button
          type="button"
          tabIndex={-1}
          disabled={outOfRange(todayValue, todayValue.length)}
          onClick={() => onSelect(todayValue)}
          className="rounded-md px-2 py-1 text-sm font-medium text-academy-700 hover:bg-ink-100 disabled:text-ink-300"
        >
          {mode === 'month' ? 'Tháng này' : 'Hôm nay'}
        </button>
        {onClear ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={onClear}
            className="rounded-md px-2 py-1 text-sm text-ink-500 hover:bg-ink-50 hover:text-ink-700"
          >
            Xoá
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface DateInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
  // Defaults to clearable unless required.
  clearable?: boolean;
}

interface PickerFieldProps extends DateInputProps {
  mode: 'date' | 'month';
}

function PickerField({
  mode,
  label,
  value,
  onChange,
  min,
  max,
  error,
  hint,
  required,
  disabled,
  id,
  name,
  placeholder,
  className,
  clearable,
  'aria-label': ariaLabel,
}: PickerFieldProps) {
  const reactId = React.useId();
  const inputId = id ?? name ?? reactId;
  const format = mode === 'month' ? formatDisplayMonth : formatDisplayDate;
  const parse = mode === 'month' ? parseDisplayMonth : parseDisplayDate;
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [text, setText] = React.useState(() => format(value));
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  // Phones get the calendar only, without the on-screen keyboard covering it.
  const [touch] = React.useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches));
  const canClear = (clearable ?? !required) && Boolean(value) && !disabled;

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) setText(format(value));
  }, [value, format]);

  const place = React.useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const height = popoverRef.current?.offsetHeight ?? 340;
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = rect.bottom + GAP;
    const top = below + height > window.innerHeight - 8 && rect.top - GAP - height > 8 ? rect.top - GAP - height : below;
    setPosition({ top, left });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // Measure again once the popover has its real height.
  React.useLayoutEffect(() => {
    if (open && position) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position === null]);

  const inRange = (key: string) => !((min && key < min.slice(0, key.length)) || (max && key > max.slice(0, key.length)));

  const commitText = () => {
    if (!text.trim()) {
      if (value && (clearable ?? !required)) onChange('');
      else setText(format(value));
      return;
    }
    const parsed = parse(text);
    if (parsed && inRange(parsed)) {
      if (parsed !== value) onChange(parsed);
      setText(format(parsed));
    } else {
      setText(format(value));
    }
  };

  const select = (next: string) => {
    setText(format(next));
    setOpen(false);
    if (next !== value) onChange(next);
  };

  const close = () => {
    setOpen(false);
    setPosition(null);
  };

  const describedBy = error || hint ? `${inputId}-help` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? (
        <label htmlFor={inputId} className="block text-sm font-medium text-ink-700">
          {label}
        </label>
      ) : null}
      <div ref={anchorRef} className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          readOnly={touch}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-haspopup="dialog"
          aria-expanded={open}
          placeholder={placeholder ?? (mode === 'month' ? 'mm/yyyy' : 'dd/mm/yyyy')}
          maxLength={mode === 'month' ? 7 : 10}
          value={text}
          onChange={event => setText(autoSlash(text, event.target.value, mode === 'month' ? 1 : 2))}
          onClick={() => !disabled && setOpen(true)}
          onBlur={() => {
            commitText();
            close();
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitText();
              close();
            } else if (event.key === 'Escape' && open) {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              close();
            } else if (event.key === 'ArrowDown') {
              setOpen(true);
            }
          }}
          className={cn(
            'block min-h-10 w-full rounded-lg border bg-white py-2 pl-3 text-sm tabular-nums text-ink-900 transition-colors duration-200 placeholder:text-ink-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-500',
            touch && !disabled && 'cursor-pointer',
            canClear ? 'pr-16' : 'pr-10',
            error
              ? 'border-danger-100 focus:border-danger-600 focus:ring-danger-100'
              : 'border-ink-200 focus:border-academy-500 focus:ring-academy-100',
          )}
        />
        <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
          {canClear ? (
            <button
              type="button"
              tabIndex={-1}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setText('');
                onChange('');
              }}
              className="flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:bg-ink-50 hover:text-ink-600"
              aria-label="Xoá ngày"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              inputRef.current?.focus();
              if (open) close();
              else setOpen(true);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-academy-700 disabled:pointer-events-none"
            aria-label={mode === 'month' ? 'Mở chọn tháng' : 'Mở lịch'}
          >
            <Calendar className="h-4 w-4" />
          </button>
        </div>
      </div>
      {error ? (
        <p id={describedBy} className="text-sm text-danger-600">{error}</p>
      ) : hint ? (
        <p id={describedBy} className="text-sm text-ink-500">{hint}</p>
      ) : null}
      {open
        ? createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={mode === 'month' ? 'Chọn tháng' : 'Chọn ngày'}
            // Keep focus in the text field so blur still means "done".
            onMouseDown={event => event.preventDefault()}
            onClick={event => event.stopPropagation()}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: Math.min(POPOVER_WIDTH, typeof window !== 'undefined' ? window.innerWidth - 16 : POPOVER_WIDTH),
            }}
            className="z-[80] rounded-xl border border-ink-200 bg-white p-3 shadow-raised"
          >
            <CalendarPanel
              mode={mode}
              value={value}
              min={min}
              max={max}
              onSelect={select}
              onClear={canClear ? () => select('') : undefined}
            />
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

export function DateInput(props: DateInputProps) {
  return <PickerField {...props} mode="date" />;
}

export function MonthInput(props: DateInputProps) {
  return <PickerField {...props} mode="month" />;
}

// "1715", "17:15" or "7:15" → "17:15"; null when it is not a time.
export function parseTimeText(text: string): string | null {
  const trimmed = text.trim();
  const match = /^(\d{1,2}):?(\d{2})$/.exec(trimmed);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${pad(hours)}:${pad(minutes)}`;
}

export interface TimeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
}

// 24-hour "HH:mm" text field; the browser's time input shows AM/PM on English devices.
export function TimeInput({ value, onChange, className, onBlur, ...props }: TimeInputProps) {
  const [text, setText] = React.useState(value);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) setText(value);
  }, [value]);

  return (
    <input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={props.placeholder ?? 'HH:mm'}
      maxLength={5}
      value={text}
      onChange={event => {
        // Typing ":" right after the auto-added one must not double it.
        const next = event.target.value.replace(/[^\d:]/g, '').replace(/:+/g, ':');
        const withColon = next.length > text.length && /^\d{2}$/.test(next) ? `${next}:` : next;
        setText(withColon);
        const parsed = parseTimeText(withColon);
        if (parsed && withColon.length >= 4) onChange(parsed);
      }}
      onBlur={event => {
        const parsed = parseTimeText(text);
        if (parsed) {
          setText(parsed);
          if (parsed !== value) onChange(parsed);
        } else {
          setText(value);
        }
        onBlur?.(event);
      }}
      className={cn('tabular-nums', className)}
    />
  );
}
